import { canonicalItem, type CartItem, type MenuProduct, type OrderView } from '../shared/domain';

export interface SavedFavorite { id: string; name: string; item: CartItem; savedAt: number }
export interface SavedRecentOrder { id: string; number: string; paidAt: number; items: { name: string; item: CartItem }[] }
export interface SavedDrinks { version: 1; favorites: SavedFavorite[]; recentOrders: SavedRecentOrder[] }
export type SavedItemIssue = 'missing' | 'unavailable' | 'bean' | 'ice' | 'sweetness' | 'extras' | 'packaging' | 'quantity';
export const MAX_FAVORITES = 30;
export const MAX_RECENT_ORDERS = 10;
const MAX_ORDER_LINES = 50;

export function savedDrinksKey(mode: string, identity?: string): string {
  return `tequisa-saved-drinks-${JSON.stringify([mode, identity ?? 'guest'])}`;
}
export function emptySavedDrinks(): SavedDrinks { return { version: 1, favorites: [], recentOrders: [] }; }

function record(value: unknown): value is Record<string, unknown> { return !!value && typeof value === 'object' && !Array.isArray(value); }
function id(value: unknown): value is string { return typeof value === 'string' && !!value.trim() && value.length <= 200; }
function displayName(value: unknown): string | null {
  return typeof value === 'string' && !!value.trim() && value.length <= 160 ? value.replace(/[\u0000-\u001f\u007f]/g, '').trim() || null : null;
}
function timestamp(value: unknown, now: number): value is number { return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= now; }
/** Copy only customer selections. Quotes, costs and order tracking credentials never enter storage. */
function copyItem(value: unknown): CartItem | null {
  if (!record(value) || !id(value.productId) || !Number.isInteger(value.quantity) || Number(value.quantity) < 1 || Number(value.quantity) > 20 || !record(value.selections)) return null;
  const selections = value.selections;
  if (selections.beanId !== undefined && !id(selections.beanId) || !['none', 'less', 'normal', 'extra'].includes(String(selections.ice)) ||
    typeof selections.sweetness !== 'number' || !Number.isFinite(selections.sweetness) || selections.sweetness < 0 || selections.sweetness > 100 ||
    !['takeaway', 'own_cup'].includes(String(selections.packaging)) || !Array.isArray(selections.extras) || selections.extras.length > 50 ||
    !selections.extras.every(id) || new Set(selections.extras).size !== selections.extras.length) return null;
  return { productId: value.productId, quantity: Number(value.quantity), selections: {
    ...(selections.beanId !== undefined ? { beanId: selections.beanId as string } : {}),
    ice: selections.ice as CartItem['selections']['ice'], sweetness: selections.sweetness,
    packaging: selections.packaging as CartItem['selections']['packaging'], extras: [...selections.extras] as string[],
  } };
}
function cleanState(value: unknown, now = Date.now()): SavedDrinks {
  if (!record(value) || value.version !== 1 || !Array.isArray(value.favorites) || !Array.isArray(value.recentOrders)) return emptySavedDrinks();
  const favorites: SavedFavorite[] = [];
  const recentOrders: SavedRecentOrder[] = [];
  for (const entry of value.favorites.slice(0, MAX_FAVORITES)) {
    if (!record(entry) || !id(entry.id) || !timestamp(entry.savedAt, now) || favorites.some(item => item.id === entry.id)) continue;
    const item = copyItem(entry.item); const name = displayName(entry.name);
    if (item && name) favorites.push({ id: entry.id, name, item, savedAt: entry.savedAt });
  }
  for (const entry of value.recentOrders.slice(0, MAX_RECENT_ORDERS)) {
    if (!record(entry) || !id(entry.id) || !timestamp(entry.paidAt, now) || !Array.isArray(entry.items) ||
      !entry.items.length || entry.items.length > MAX_ORDER_LINES || recentOrders.some(order => order.id === entry.id)) continue;
    const number = displayName(entry.number);
    const items = entry.items.map(line => { if (!record(line)) return null; const name = displayName(line.name); const item = copyItem(line.item); return name && item ? { name, item } : null; });
    // An order is a single reorder unit: never silently drop one malformed line.
    if (number && items.every(line => line !== null)) recentOrders.push({ id: entry.id, number, paidAt: entry.paidAt, items: items as SavedRecentOrder['items'] });
  }
  return { version: 1, favorites, recentOrders };
}
export function readSavedDrinks(key: string): SavedDrinks {
  // Access errors are distinct from malformed content: callers can explain that this browser cannot save.
  const raw = localStorage.getItem(key);
  try {
    return raw && raw.length <= 200_000 ? cleanState(JSON.parse(raw)) : emptySavedDrinks();
  } catch { return emptySavedDrinks(); }
}
/** Throws on failed persistence so the UI can show an error without claiming a successful save. */
export function writeSavedDrinks(key: string, value: SavedDrinks): void {
  localStorage.setItem(key, JSON.stringify(cleanState(value)));
}
export function saveFavorite(state: SavedDrinks, value: CartItem, valueName: string, existingId?: string): SavedDrinks {
  const item = copyItem(value); const name = displayName(valueName);
  if (!item || !name) throw new Error('Invalid favorite');
  const saved = cleanState(state);
  const match = saved.favorites.find(favorite => favorite.id === existingId) ?? saved.favorites.find(favorite => canonicalItem(favorite.item) === canonicalItem(item));
  const favorite: SavedFavorite = { id: match?.id ?? crypto.randomUUID(), name, item, savedAt: Date.now() };
  const favorites = [favorite, ...saved.favorites.filter(entry => entry.id !== favorite.id && canonicalItem(entry.item) !== canonicalItem(item))];
  if (favorites.length > MAX_FAVORITES) throw new Error('Favorite limit reached');
  return { ...saved, favorites };
}
export function removeFavorite(state: SavedDrinks, favoriteId: string): SavedDrinks {
  const saved = cleanState(state); return { ...saved, favorites: saved.favorites.filter(favorite => favorite.id !== favoriteId) };
}
/** Call only with the authoritative order view; a checkout callback or cart is not proof of payment. */
export function rememberPaidOrder(state: SavedDrinks, order: OrderView): SavedDrinks {
  if (order.paymentStatus !== 'paid' || !id(order.id) || state.recentOrders.some(entry => entry.id === order.id)) return state;
  const paidAt = order.paidAt ?? order.createdAt; const number = displayName(order.number);
  if (!timestamp(paidAt, Date.now()) || !number || !order.lines.length || order.lines.length > MAX_ORDER_LINES) return state;
  const items = order.lines.map(line => { const name = displayName(line.name); const item = copyItem(line); return name && item ? { name, item } : null; });
  if (items.some(item => item === null)) return state;
  return { ...cleanState(state), recentOrders: [{ id: order.id, number, paidAt, items: items as SavedRecentOrder['items'] }, ...cleanState(state).recentOrders].slice(0, MAX_RECENT_ORDERS) };
}
/** Validate against today's public menu. Adding to cart must still request the current authoritative quote. */
export function validateSavedItem(item: CartItem, products: MenuProduct[]): { item: CartItem; product?: MenuProduct; issues: SavedItemIssue[]; valid: boolean } {
  const product = products.find(entry => entry.id === item.productId);
  const issues: SavedItemIssue[] = [];
  if (!Number.isInteger(item.quantity) || item.quantity < 1 || item.quantity > 20) issues.push('quantity');
  if (!product) issues.push('missing');
  else {
    if (!product.available) issues.push('unavailable');
    const selection = item.selections;
    const bean = product.options.beans.find(entry => entry.id === selection.beanId);
    if (product.options.beans.length ? !bean || bean.available === false : !!selection.beanId) issues.push('bean');
    if (!product.options.ice.includes(selection.ice)) issues.push('ice');
    if (!product.options.sweetness.includes(selection.sweetness)) issues.push('sweetness');
    if (selection.extras.some(extra => !product.options.extras.some(entry => entry.id === extra)) || new Set(selection.extras).size !== selection.extras.length) issues.push('extras');
    if (!['takeaway', 'own_cup'].includes(selection.packaging)) issues.push('packaging');
  }
  return { item: { productId: item.productId, quantity: item.quantity, selections: { ...item.selections, extras: [...item.selections.extras] } }, product, issues, valid: !issues.length };
}
